import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
  Pagination,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Add,
  Edit,
  Delete,
  Search,
  Category,
  Inventory2,
  Visibility,
  VisibilityOff,
  ExpandMore as ExpandMoreIcon,
  CalendarToday,
  Description,
} from '@mui/icons-material';
import { categoriasService, CategoriaResponse, CategoriaBase } from '../../../services/categorias';

// Styled Components con diseño El Piñón
const DashboardContainer = styled(Box)({
  background: 'linear-gradient(135deg, #f8fffe 0%, #e8f5e8 100%)',
  minHeight: '100vh',
  padding: '24px',
  '@media (max-width: 768px)': {
    padding: '16px',
  },
});

// Componentes para vista móvil responsiva
const MobileCard = styled(Box)({
  background: 'linear-gradient(145deg, #ffffff 0%, #f8fffe 50%, #f0f9f0 100%)',
  borderRadius: '16px',
  marginBottom: '16px',
  overflow: 'hidden',
  boxShadow: '0 4px 15px rgba(46, 90, 51, 0.08)',
  border: '1px solid rgba(46, 90, 51, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(46, 90, 51, 0.15)',
  },
});

const MobileCardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px',
  background: 'linear-gradient(135deg, rgba(46, 90, 51, 0.03) 0%, rgba(61, 115, 66, 0.06) 100%)',
  borderBottom: '1px solid rgba(46, 90, 51, 0.08)',
  cursor: 'pointer',
});

const ExpandIcon = styled(Box)<{ expanded: boolean }>(({ expanded }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  background: expanded ? 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)' : 'rgba(46, 90, 51, 0.1)',
  color: expanded ? '#fff' : '#2e5a33',
  transition: 'all 0.3s ease',
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
}));

const MobileCardContent = styled(Box)<{ expanded: boolean }>(({ expanded }) => ({
  maxHeight: expanded ? '500px' : '0',
  opacity: expanded ? 1 : 0,
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  padding: expanded ? '16px' : '0 16px',
}));

const DetailItem = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  margin: '8px 0',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #f8fffe 0%, #f0f9f0 100%)',
  border: '1px solid rgba(46, 90, 51, 0.06)',
});

const ActionsContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '12px',
  padding: '16px',
  marginTop: '8px',
  borderTop: '1px solid rgba(46, 90, 51, 0.1)',
  background: 'linear-gradient(135deg, rgba(46, 90, 51, 0.02) 0%, rgba(61, 115, 66, 0.04) 100%)',
  flexWrap: 'wrap',
});

const HeaderSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '32px',
  padding: '24px',
  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
  borderRadius: '20px',
  color: 'white',
  boxShadow: '0 8px 32px rgba(46, 90, 51, 0.3)',
});

const StatsCard = styled(Card)({
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  borderRadius: '16px',
  border: '2px solid #e8f5e8',
  boxShadow: '0 4px 20px rgba(46, 90, 51, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 30px rgba(46, 90, 51, 0.2)',
    borderColor: '#2e5a33',
  },
});

const ActionButton = styled(Button)({
  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
  color: 'white',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 24px',
  boxShadow: '0 4px 15px rgba(46, 90, 51, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #3d7342 0%, #2e5a33 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(46, 90, 51, 0.4)',
  },
});

// Interface para mostrar (usar directamente CategoriaResponse)
type CategoriaDisplay = CategoriaResponse;

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle }) => (
  <StatsCard>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h4" fontWeight={700} color={color} gutterBottom>
            {value}
          </Typography>
          <Typography variant="h6" color="#2e5a33" fontWeight={600}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar
          sx={{
            backgroundColor: `${color}15`,
            color: color,
            width: 64,
            height: 64,
          }}
        >
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </StatsCard>
);

const DashboardCategorias: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [categorias, setCategorias] = useState<CategoriaDisplay[]>([]);
  const [filteredCategorias, setFilteredCategorias] = useState<CategoriaDisplay[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingCategoria, setEditingCategoria] = useState<CategoriaDisplay | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    activo: true,
  });

  // Cargar categorías desde Django API
  useEffect(() => {
    loadCategorias();
  }, []);

  const loadCategorias = async () => {
    try {
      setLoading(true);
      // Obtener categorías desde el servicio CRUD
      const data = await categoriasService.getAll();
      
      // El servicio ya maneja la paginación y devuelve un array
      setCategorias(data);
      console.log('✅ Categorías cargadas desde Django:', data.length);
    } catch (error) {
      console.error('❌ Error al cargar categorías:', error);
      setCategorias([]);
      setSnackbar({ open: true, message: 'Error al cargar categorías', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Search filter
  useEffect(() => {
    if (!Array.isArray(categorias)) {
      setFilteredCategorias([]);
      return;
    }
    
    const filtered = categorias.filter(categoria =>
      categoria.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      categoria.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCategorias(filtered);
    setCurrentPage(1); // Resetear a página 1 al buscar
  }, [searchTerm, categorias]);

  // Calcular categorías paginadas
  const totalPages = Math.ceil(filteredCategorias.length / ITEMS_PER_PAGE);
  const paginatedCategorias = filteredCategorias.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleOpenDialog = (categoria?: CategoriaDisplay) => {
    if (categoria) {
      setEditingCategoria(categoria);
      setFormData({
        nombre: categoria.nombre,
        descripcion: categoria.descripcion || '',
        activo: categoria.activo,
      });
    } else {
      setEditingCategoria(null);
      setFormData({
        nombre: '',
        descripcion: '',
        activo: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCategoria(null);
  };

  const handleSave = async () => {
    try {
      // Validaciones
      if (!formData.nombre.trim()) {
        setSnackbar({ open: true, message: 'El nombre es requerido', severity: 'error' });
        return;
      }

      const categoriaData: CategoriaBase = {
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion.trim() || undefined,
        activo: formData.activo,
      };

      if (editingCategoria) {
        // Actualizar categoría existente
        await categoriasService.update(editingCategoria.id, categoriaData);
        setSnackbar({ open: true, message: '✅ Categoría actualizada exitosamente', severity: 'success' });
      } else {
        // Crear nueva categoría
        await categoriasService.create(categoriaData);
        setSnackbar({ open: true, message: '✅ Categoría creada exitosamente', severity: 'success' });
      }

      handleCloseDialog();
      loadCategorias();
    } catch (error) {
      console.error('Error al guardar categoría:', error);
      setSnackbar({ open: true, message: `❌ ${error}`, severity: 'error' });
    }
  };

  const toggleEstado = async (id: number) => {
    try {
      await categoriasService.toggleStatus(id);
      setSnackbar({ open: true, message: '✅ Estado actualizado exitosamente', severity: 'success' });
      loadCategorias();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      setSnackbar({ open: true, message: `❌ ${error}`, severity: 'error' });
    }
  };

  const eliminarCategoria = async (id: number) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta categoría?')) {
      return;
    }

    try {
      await categoriasService.delete(id);
      setSnackbar({ open: true, message: '✅ Categoría eliminada exitosamente', severity: 'success' });
      loadCategorias();
    } catch (error) {
      console.error('Error al eliminar:', error);
      setSnackbar({ open: true, message: `❌ ${error}`, severity: 'error' });
    }
  };

  const stats = {
    total: Array.isArray(categorias) ? categorias.length : 0,
    activas: Array.isArray(categorias) ? categorias.filter(c => c.activo).length : 0,
    inactivas: Array.isArray(categorias) ? categorias.filter(c => !c.activo).length : 0,
    totalProductos: 0, // Se actualiza cuando se implementen productos
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Cargando categorías...</Typography>
      </Box>
    );
  }

  return (
    <DashboardContainer>
      {/* Header */}
      <HeaderSection>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            src="/assets/logos/logo-el-pinon-nuevo.png"
            sx={{ width: 60, height: 60, border: '3px solid rgba(255,255,255,0.3)' }}
          />
          <Box>
            <Typography variant="h4" fontWeight={700} fontFamily="'Playfair Display', serif">
              Gestión de Categorías - El Piñón
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Administra las categorías de productos de tu tienda
            </Typography>
          </Box>
        </Box>
        <ActionButton onClick={() => handleOpenDialog()}>
          <Add sx={{ mr: 1 }} />
          Nueva Categoría
        </ActionButton>
      </HeaderSection>

      {/* Stats Cards */}
      <Box display="flex" gap={3} marginBottom={4} flexWrap="wrap">
        <Box flex="1 1 250px">
          <StatCard
            title="Total Categorías"
            value={stats.total.toString()}
            icon={<Category />}
            color="#2e5a33"
            subtitle="Todas las categorías"
          />
        </Box>
        <Box flex="1 1 250px">
          <StatCard
            title="Categorías Activas"
            value={stats.activas.toString()}
            icon={<Visibility />}
            color="#4caf50"
            subtitle="En uso actualmente"
          />
        </Box>
        <Box flex="1 1 250px">
          <StatCard
            title="Categorías Inactivas"
            value={stats.inactivas.toString()}
            icon={<VisibilityOff />}
            color="#ff9800"
            subtitle="Deshabilitadas"
          />
        </Box>
        <Box flex="1 1 250px">
          <StatCard
            title="Total Productos"
            value={stats.totalProductos.toString()}
            icon={<Inventory2 />}
            color="#9c27b0"
            subtitle="En todas las categorías"
          />
        </Box>
      </Box>

      {/* Search and Table */}
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: '16px', boxShadow: '0 4px 20px rgba(46, 90, 51, 0.1)' }}>
        <Box mb={3}>
          <TextField
            fullWidth
            placeholder="Buscar categorías..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '&:hover fieldset': {
                  borderColor: '#2e5a33',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2e5a33',
                },
              },
            }}
          />
        </Box>

        {/* Vista Responsiva */}
        {isMobile ? (
          // Vista Móvil - Tarjetas con acordeón
          <Box>
            {paginatedCategorias.length === 0 ? (
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  py: 6,
                  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
                  borderRadius: '16px',
                  border: '2px dashed rgba(46, 90, 51, 0.2)',
                }}
              >
                <Category sx={{ fontSize: 64, color: 'rgba(46, 90, 51, 0.3)', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  No hay categorías disponibles
                </Typography>
              </Box>
            ) : (
              paginatedCategorias.map((categoria) => (
                <MobileCard key={categoria.id}>
                  <MobileCardHeader onClick={() => setExpandedCard(expandedCard === categoria.id ? null : categoria.id)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: categoria.activo ? '#2e5a33' : '#9e9e9e',
                          width: 48,
                          height: 48,
                        }}
                      >
                        <Category />
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="subtitle1" fontWeight={600} color="#2e5a33">
                          {categoria.nombre}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Chip
                            label={categoria.activo ? 'Activa' : 'Inactiva'}
                            color={categoria.activo ? 'success' : 'default'}
                            size="small"
                            onClick={(e) => { e.stopPropagation(); toggleEstado(categoria.id); }}
                            sx={{ cursor: 'pointer' }}
                          />
                        </Box>
                      </Box>
                    </Box>
                    <ExpandIcon expanded={expandedCard === categoria.id}>
                      <ExpandMoreIcon fontSize="small" />
                    </ExpandIcon>
                  </MobileCardHeader>
                  
                  <MobileCardContent expanded={expandedCard === categoria.id}>
                    <DetailItem>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Description sx={{ color: '#2e5a33', fontSize: 20 }} />
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>Descripción</Typography>
                      </Box>
                      <Typography fontWeight={500} sx={{ textAlign: 'right', maxWidth: '60%' }}>
                        {categoria.descripcion || 'Sin descripción'}
                      </Typography>
                    </DetailItem>
                    
                    <DetailItem>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday sx={{ color: '#2e5a33', fontSize: 20 }} />
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>Fecha creación</Typography>
                      </Box>
                      <Typography fontWeight={500}>
                        {categoria.fecha_creacion ? new Date(categoria.fecha_creacion).toLocaleDateString('es-CL') : 'N/A'}
                      </Typography>
                    </DetailItem>
                    
                    <ActionsContainer>
                      <IconButton
                        sx={{ 
                          bgcolor: '#2e5a33', 
                          color: 'white',
                          '&:hover': { bgcolor: '#3d7342' }
                        }}
                        onClick={() => handleOpenDialog(categoria)}
                        title="Editar categoría"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        sx={{ 
                          bgcolor: '#d32f2f', 
                          color: 'white',
                          '&:hover': { bgcolor: '#b71c1c' }
                        }}
                        onClick={() => eliminarCategoria(categoria.id)}
                        title="Eliminar categoría"
                      >
                        <Delete />
                      </IconButton>
                    </ActionsContainer>
                  </MobileCardContent>
                </MobileCard>
              ))
            )}
          </Box>
        ) : (
          // Vista Desktop - Tabla tradicional
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: '#2e5a33' }}>Categoría</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#2e5a33' }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#2e5a33' }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#2e5a33' }}>Fecha Creación</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#2e5a33' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(paginatedCategorias) && paginatedCategorias.map((categoria) => (
                  <TableRow key={categoria.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar 
                          sx={{ 
                            backgroundColor: '#2e5a33', 
                            color: 'white',
                            width: 40,
                            height: 40 
                          }}
                        >
                          <Category />
                        </Avatar>
                        <Typography fontWeight={600}>{categoria.nombre}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {categoria.descripcion || 'Sin descripción'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={categoria.activo ? 'Activa' : 'Inactiva'}
                        color={categoria.activo ? 'success' : 'default'}
                        size="small"
                        onClick={() => toggleEstado(categoria.id)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell>
                      {categoria.fecha_creacion ? new Date(categoria.fecha_creacion).toLocaleDateString('es-CL') : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(categoria)}
                          sx={{ color: '#2e5a33' }}
                          title="Editar categoría"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => eliminarCategoria(categoria.id)}
                          sx={{ color: '#d32f2f' }}
                          title="Eliminar categoría"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Paginación */}
        {filteredCategorias.length > ITEMS_PER_PAGE && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3, gap: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredCategorias.length)} de {filteredCategorias.length} categorías
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 500,
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
                    color: 'white',
                  },
                },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ backgroundColor: '#2e5a33', color: 'white' }}>
              <Category />
            </Avatar>
            <Typography variant="h6" fontWeight={600}>
              {editingCategoria ? 'Editar Categoría' : 'Nueva Categoría'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2 }}>
            <TextField
              label="Nombre de la categoría *"
              fullWidth
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              required
            />
            <TextField
              label="Descripción"
              fullWidth
              multiline
              rows={3}
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
            />
            <Box display="flex" alignItems="center" gap={1}>
              <Typography>Estado:</Typography>
              <Chip
                label={formData.activo ? 'Activa' : 'Inactiva'}
                color={formData.activo ? 'success' : 'default'}
                onClick={() => setFormData({ ...formData, activo: !formData.activo })}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <ActionButton onClick={handleSave}>
            {editingCategoria ? 'Actualizar' : 'Crear'}
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardContainer>
  );
};

export default DashboardCategorias;